import React from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { RotateCcw, ChevronDown, X } from "lucide-react";

const STATUS_LABELS = {
    pending:    "În așteptare",
    approved:   "Aprobat",
    rejected:   "Respins",
    processing: "În procesare",
    completed:  "Finalizat",
};

const STATUS_COLORS = {
    pending:    "bg-yellow-100 text-yellow-800",
    approved:   "bg-blue-100 text-blue-800",
    rejected:   "bg-red-100 text-red-800",
    processing: "bg-purple-100 text-purple-800",
    completed:  "bg-green-100 text-green-800",
};

export default function AdminReturns() {
    const [returns, setReturns] = React.useState([]);
    const [filter, setFilter] = React.useState("");
    const [selected, setSelected] = React.useState(null);
    const [noteText, setNoteText] = React.useState("");
    const [saving, setSaving] = React.useState(false);

    const load = React.useCallback(() => {
        api.get("/admin/returns", { params: filter ? { status: filter } : {} })
            .then(r => setReturns(r.data || []));
    }, [filter]);

    React.useEffect(() => { load(); }, [load]);

    const openDetail = (r) => { setSelected(r); setNoteText(r.admin_notes || ""); };

    const update = async (status) => {
        setSaving(true);
        try {
            await api.put(`/admin/returns/${selected.return_id}`, { status, admin_notes: noteText });
            toast.success("Actualizat");
            load();
            setSelected(prev => ({ ...prev, status, admin_notes: noteText }));
        } catch { toast.error("Eroare"); }
        finally { setSaving(false); }
    };

    const saveNote = async () => {
        setSaving(true);
        try {
            await api.put(`/admin/returns/${selected.return_id}`, { admin_notes: noteText });
            toast.success("Notă salvată");
            load();
        } catch { toast.error("Eroare"); }
        finally { setSaving(false); }
    };

    const pendingCount = returns.filter(r => r.status === "pending").length;

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="font-heading text-2xl">Retururi</h1>
                    {pendingCount > 0 && <p className="text-sm text-muted-foreground mt-0.5">{pendingCount} în așteptare</p>}
                </div>
                <div className="flex items-center gap-2">
                    <select value={filter} onChange={e => setFilter(e.target.value)} className="input-soft !py-2 text-sm">
                        <option value="">Toate statusurile</option>
                        {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                </div>
            </div>

            {returns.length === 0 && (
                <div className="py-16 text-center text-muted-foreground">
                    <RotateCcw className="h-10 w-10 mx-auto mb-3 opacity-30" />
                    <p>Nu există cereri de retur{filter ? ` cu status «${STATUS_LABELS[filter]}»` : ""}.</p>
                </div>
            )}

            <div className="space-y-3">
                {returns.map(r => (
                    <button key={r.return_id} type="button" onClick={() => openDetail(r)}
                        className="w-full text-left bg-card border border-border/60 rounded-2xl p-4 hover:border-primary/40 hover:shadow-md transition-all">
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="font-mono text-xs text-muted-foreground">#{r.order_number}</span>
                                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATUS_COLORS[r.status] || "bg-muted text-muted-foreground"}`}>
                                        {STATUS_LABELS[r.status] || r.status}
                                    </span>
                                </div>
                                <div className="font-semibold text-sm">{r.user_name} <span className="text-muted-foreground font-normal">({r.user_email})</span></div>
                                <div className="text-xs text-muted-foreground mt-0.5">{r.reason}</div>
                                <div className="text-xs text-muted-foreground mt-1">{r.items?.length} produs(e) · {new Date(r.created_at).toLocaleDateString("ro-RO")}</div>
                            </div>
                            <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-1 -rotate-90" />
                        </div>
                    </button>
                ))}
            </div>

            {/* Detail modal */}
            {selected && (
                <div className="fixed inset-0 z-[200] bg-foreground/60 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4">
                    <div className="bg-card rounded-t-2xl sm:rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
                        <div className="p-5 border-b border-border flex items-center justify-between">
                            <div>
                                <h3 className="font-heading text-xl">Retur — Comanda #{selected.order_number}</h3>
                                <div className="text-xs text-muted-foreground">{selected.user_name} · {selected.user_email}</div>
                            </div>
                            <button onClick={() => setSelected(null)} className="btn-ghost"><X className="h-5 w-5" /></button>
                        </div>
                        <div className="p-5 space-y-4">
                            {/* Status badge */}
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-muted-foreground">Status curent:</span>
                                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_COLORS[selected.status]}`}>
                                    {STATUS_LABELS[selected.status]}
                                </span>
                            </div>

                            {/* Items */}
                            <div className="bg-muted rounded-xl p-3 space-y-2">
                                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Produse returnate</div>
                                {(selected.items || []).map((it, i) => (
                                    <div key={i} className="flex justify-between text-sm">
                                        <span className="truncate">{it.name}</span>
                                        <span className="font-medium flex-shrink-0 ml-2">× {it.quantity}</span>
                                    </div>
                                ))}
                            </div>

                            {/* Reason */}
                            <div>
                                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Motiv</div>
                                <div className="text-sm">{selected.reason}</div>
                                {selected.description && <div className="text-sm text-muted-foreground mt-1">{selected.description}</div>}
                            </div>

                            {/* Admin note */}
                            <div>
                                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide block mb-1">Notă internă / răspuns client</label>
                                <textarea rows={3} value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Instrucțiuni pentru client, decizie, etc." className="input-soft text-sm" />
                                <button type="button" onClick={saveNote} disabled={saving} className="btn-outline !py-1.5 text-xs mt-2">Salvează nota</button>
                            </div>

                            {/* Actions */}
                            <div>
                                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Actualizează status</div>
                                <div className="grid grid-cols-2 gap-2">
                                    {[
                                        { s: "approved",   label: "Aprobă",       cls: "btn-primary" },
                                        { s: "rejected",   label: "Respinge",     cls: "btn-outline !text-destructive !border-destructive" },
                                        { s: "processing", label: "În procesare", cls: "btn-outline" },
                                        { s: "completed",  label: "Finalizat",    cls: "btn-outline !border-green-500 !text-green-700" },
                                    ].map(({ s, label, cls }) => (
                                        <button key={s} type="button" disabled={saving || selected.status === s}
                                            onClick={() => update(s)}
                                            className={`${cls} !py-2 text-sm disabled:opacity-40`}>
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
